import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  InputAdornment,
  Link,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MetricsStrip from "../../components/MetricsStrip";
import RowActionsMenu from "../../components/RowActionsMenu";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const getTicketCount = (tag, ticketCounts = {}) => {
  const countedTickets = ticketCounts[Number(tag.id)];

  if (typeof countedTickets === "number") {
    return countedTickets;
  }

  if (Array.isArray(tag.ticketTags)) {
    return tag.ticketTags.length;
  }

  if (typeof tag.ticketCount === "number") {
    return tag.ticketCount;
  }

  return 0;
};

const hasAutomation = (tag) =>
  Boolean(tag.nextLaneId || tag.rollbackLaneId || tag.timeLane || tag.greetingMessageLane);

const moveTag = (tags, sourceId, targetId, position = "before") => {
  if (!sourceId || !targetId || Number(sourceId) === Number(targetId)) {
    return tags;
  }

  const sourceIndex = tags.findIndex((tag) => Number(tag.id) === Number(sourceId));
  const sourceTag = tags[sourceIndex];

  if (!sourceTag) {
    return tags;
  }

  const withoutSource = tags.filter((tag) => Number(tag.id) !== Number(sourceId));
  const targetIndex = withoutSource.findIndex((tag) => Number(tag.id) === Number(targetId));

  if (targetIndex < 0) {
    return tags;
  }

  const insertIndex = position === "after" ? targetIndex + 1 : targetIndex;

  return [
    ...withoutSource.slice(0, insertIndex),
    sourceTag,
    ...withoutSource.slice(insertIndex),
  ];
};

const applyKanbanOrder = (tags) =>
  tags.map((tag, index) => ({
    ...tag,
    kanbanOrder: index + 1,
  }));

const buildQueueIds = (queues = []) =>
  queues
    .map((queue) => Number(queue?.UserQueue?.queueId || queue?.id))
    .filter((queueId) => Number.isInteger(queueId) && queueId > 0);

const countTicketsByKanbanTag = (tickets = []) => {
  const counts = {};

  tickets.forEach((ticket) => {
    const tagIds = new Set();

    (ticket.tags || []).forEach((tag) => {
      const tagId = Number(tag.id);

      if (Number(tag.kanban) === 1 && Number.isInteger(tagId) && tagId > 0) {
        tagIds.add(tagId);
      }
    });

    if (tagIds.size === 0) {
      (ticket.ticketTags || []).forEach((ticketTag) => {
        const tagId = Number(ticketTag.tagId);

        if (Number.isInteger(tagId) && tagId > 0) {
          tagIds.add(tagId);
        }
      });
    }

    tagIds.forEach((tagId) => {
      counts[tagId] = (counts[tagId] || 0) + 1;
    });
  });

  return counts;
};

const mergeTicketCounts = (baseCounts, nextCounts) => {
  const mergedCounts = { ...baseCounts };

  Object.entries(nextCounts).forEach(([tagId, count]) => {
    mergedCounts[tagId] = (mergedCounts[tagId] || 0) + count;
  });

  return mergedCounts;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  titleWrap: {
    minWidth: 0,
  },
  breadcrumbs: {
    marginBottom: theme.spacing(0.35),
    "& .MuiBreadcrumbs-separator": {
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(0.75),
      marginRight: theme.spacing(0.75),
    },
  },
  breadcrumbLink: {
    border: 0,
    padding: 0,
    background: "transparent",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.4,
    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
  breadcrumbCurrent: {
    color: theme.palette.primary.main,
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 1.4,
  },
  backLink: {
    border: 0,
    padding: 0,
    marginTop: theme.spacing(0.5),
    background: "transparent",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.78rem",
    fontWeight: 600,
    lineHeight: 1.4,
    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
    "& svg": {
      fontSize: 16,
    },
  },
  title: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      "& > *": {
        flex: "1 1 auto",
      },
    },
  },
  field: {
    width: 300,
    maxWidth: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor: fade(theme.palette.divider, 0.92),
      },
      "&:hover fieldset": {
        borderColor: fade(theme.palette.primary.main, 0.35),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.12)}`,
      },
    },
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    maxHeight: "calc(100vh - 320px)",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "none",
    },
  },
  dragCell: {
    width: 48,
    paddingRight: 0,
  },
  dragHandle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  dragHandleDisabled: {
    cursor: "default",
    opacity: 0.35,
  },
  row: {
    transition: theme.transitions.create(["background-color", "box-shadow", "opacity"], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  draggingRow: {
    opacity: 0.46,
    backgroundColor: fade(theme.palette.primary.main, 0.04),
  },
  dropBefore: {
    boxShadow: `inset 0 2px 0 ${theme.palette.primary.main}`,
  },
  dropAfter: {
    boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
  },
  columnCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  colorSwatch: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${fade(theme.palette.common.black, 0.16)}`,
    boxShadow: `inset 0 0 0 1px ${fade(theme.palette.common.white, 0.28)}`,
    flexShrink: 0,
  },
  columnName: {
    fontWeight: 600,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  orderBadge: {
    minWidth: 38,
    height: 26,
    borderRadius: 999,
    padding: theme.spacing(0.25, 1),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    backgroundColor: fade(theme.palette.text.primary, 0.07),
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
}));

const Tags = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const orderSavingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, setTags] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({});
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [draggedTagId, setDraggedTagId] = useState(null);
  const [dragOver, setDragOver] = useState({ id: null, position: "before" });
  const queueIds = useMemo(() => buildQueueIds(user?.queues), [user?.queues]);

  const fetchTags = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/tag/kanban/");
      setTags(data.lista || []);
    } catch (err) {
      toastError(err);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTicketCounts = useCallback(async () => {
    setCountsLoading(true);

    try {
      let pageNumber = 1;
      let hasMore = true;
      let nextTicketCounts = {};

      while (hasMore) {
        const { data } = await api.get("/ticket/kanban", {
          params: {
            queueIds: JSON.stringify(queueIds),
            pageNumber,
          },
        });

        nextTicketCounts = mergeTicketCounts(
          nextTicketCounts,
          countTicketsByKanbanTag(data.tickets || [])
        );
        hasMore = Boolean(data.hasMore);
        pageNumber += 1;
      }

      setTicketCounts(nextTicketCounts);
    } catch (err) {
      toastError(err);
      setTicketCounts({});
    } finally {
      setCountsLoading(false);
    }
  }, [queueIds]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    fetchTicketCounts();
  }, [fetchTicketCounts]);

  useEffect(() => {
    const onTagsEvent = () => {
      if (orderSavingRef.current) {
        return;
      }

      fetchTags();
      fetchTicketCounts();
    };

    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [fetchTags, fetchTicketCounts, socket, user.companyId]);

  useEffect(() => {
    const onTicketEvent = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTicketCounts();
      }
    };

    socket.on(`company${user.companyId}-appMessage`, onTicketEvent);

    return () => {
      socket.off(`company${user.companyId}-appMessage`, onTicketEvent);
    };
  }, [fetchTicketCounts, socket, user.companyId]);

  const filteredTags = useMemo(() => {
    const normalizedSearch = searchParam.trim().toLowerCase();

    if (!normalizedSearch) {
      return tags;
    }

    return tags.filter((tag) => tag.name?.toLowerCase().includes(normalizedSearch));
  }, [searchParam, tags]);

  const totalTickets = useMemo(
    () => tags.reduce((total, tag) => total + getTicketCount(tag, ticketCounts), 0),
    [tags, ticketCounts]
  );
  const automationCount = useMemo(() => tags.filter(hasAutomation).length, [tags]);
  const highestOrder = useMemo(
    () => tags.reduce((highest, tag) => Math.max(highest, Number(tag.kanbanOrder || 0)), 0),
    [tags]
  );

  const handleOpenTagModal = () => {
    if (!isAdmin) {
      toastError("ERR_NO_PERMISSION");
      return;
    }

    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
    fetchTags();
  };

  const handleEditTag = (tag) => {
    if (!isAdmin) {
      toastError("ERR_NO_PERMISSION");
      return;
    }

    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tagsKanban.toasts.deleted"));
      fetchTags();
    } catch (err) {
      toastError(err);
    }

    setDeletingTag(null);
    setConfirmModalOpen(false);
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  const handleRefresh = () => {
    fetchTags();
    fetchTicketCounts();
  };

  const persistOrder = async (orderedTags) => {
    if (!isAdmin) {
      toastError("ERR_NO_PERMISSION");
      return;
    }

    const tagsWithOrder = applyKanbanOrder(orderedTags);
    setTags(tagsWithOrder);
    setSavingOrder(true);
    orderSavingRef.current = true;

    try {
      await Promise.all(
        tagsWithOrder.map((tag) =>
          api.put(`/tags/${tag.id}`, {
            kanbanOrder: tag.kanbanOrder,
          })
        )
      );
      toast.success("Ordem das colunas atualizada.");
      await fetchTags();
    } catch (err) {
      toastError(err);
      await fetchTags();
    } finally {
      orderSavingRef.current = false;
      setSavingOrder(false);
      setDraggedTagId(null);
      setDragOver({ id: null, position: "before" });
    }
  };

  const handleDragStart = (event, tag) => {
    if (!isAdmin || savingOrder) {
      event.preventDefault();
      return;
    }

    setDraggedTagId(tag.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(tag.id));
  };

  const handleDragOver = (event, tag) => {
    if (!isAdmin || savingOrder) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (!draggedTagId) {
      return;
    }

    const rowRect = event.currentTarget.getBoundingClientRect();
    const position = event.clientY - rowRect.top > rowRect.height / 2 ? "after" : "before";

    if (dragOver.id !== tag.id || dragOver.position !== position) {
      setDragOver({ id: tag.id, position });
    }
  };

  const handleDrop = async (event, tag) => {
    event.preventDefault();

    if (!isAdmin || savingOrder) {
      return;
    }

    const sourceId = Number(event.dataTransfer.getData("text/plain") || draggedTagId);

    if (!sourceId) {
      return;
    }

    const nextTags = moveTag(tags, sourceId, tag.id, dragOver.position);

    if (nextTags === tags) {
      setDraggedTagId(null);
      setDragOver({ id: null, position: "before" });
      return;
    }

    await persistOrder(nextTags);
  };

  const handleDragEnd = () => {
    setDraggedTagId(null);
    setDragOver({ id: null, position: "before" });
  };

  const renderAutomation = (tag) => {
    if (!hasAutomation(tag)) {
      return <Typography color="textSecondary">-</Typography>;
    }

    return (
      <Box className={classes.chipRow}>
        {tag.nextLaneId ? <Chip size="small" label="Proxima" /> : null}
        {tag.rollbackLaneId ? <Chip size="small" label="Rollback" /> : null}
        {tag.timeLane ? <Chip size="small" label={`${tag.timeLane}h`} /> : null}
      </Box>
    );
  };

  const renderRowClassName = (tag) => {
    const classNames = [classes.row];

    if (Number(draggedTagId) === Number(tag.id)) {
      classNames.push(classes.draggingRow);
    }

    if (Number(dragOver.id) === Number(tag.id)) {
      classNames.push(dragOver.position === "after" ? classes.dropAfter : classes.dropBefore);
    }

    return classNames.join(" ");
  };

  return (
    <MainContainer fullWidth>
      <ConfirmationModal
        title={deletingTag && i18n.t("tagsKanban.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => deletingTag && handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {isAdmin && tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}

      <Box className={classes.root}>
        <Box className={classes.headerRow}>
          <Box className={classes.titleWrap}>
            <Breadcrumbs separator="/" className={classes.breadcrumbs}>
              <Link
                component="button"
                type="button"
                className={classes.breadcrumbLink}
                onClick={handleReturnToKanban}
              >
                Kanban
              </Link>
              <Typography className={classes.breadcrumbCurrent}>Colunas</Typography>
            </Breadcrumbs>
            <Typography variant="h5" className={classes.title}>
              {i18n.t("tagsKanban.title")}
            </Typography>
            <Link
              component="button"
              type="button"
              className={classes.backLink}
              onClick={handleReturnToKanban}
            >
              <ArrowBackIcon />
              Voltar para Kanban
            </Link>
          </Box>

          <Box className={classes.actionsBar}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={handleRefresh}
              disabled={loading || countsLoading || savingOrder}
            >
              Atualizar
            </Button>
            {isAdmin && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenTagModal}
                disabled={savingOrder}
              >
                {i18n.t("tagsKanban.buttons.add")}
              </Button>
            )}
          </Box>
        </Box>

        <MetricsStrip
          ariaLabel="Indicadores das colunas do Kanban"
          items={[
            { key: "columns", label: "Colunas", value: tags.length },
            { key: "tickets", label: "Tickets", value: totalTickets },
            { key: "automations", label: "Automacoes", value: automationCount },
            { key: "order", label: "Maior ordem", value: highestOrder },
          ]}
        />

        <Paper className={classes.tablePaper} elevation={0}>
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Colunas
            </Typography>
            <Box className={classes.actionsBar}>
              <TextField
                className={classes.field}
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={(event) => setSearchParam(event.target.value)}
                variant="outlined"
                size="small"
                inputProps={{ "aria-label": "Buscar colunas do Kanban" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip
                size="small"
                label={`${filteredTags.length} resultado${filteredTags.length === 1 ? "" : "s"}`}
                style={{ backgroundColor: "#eef2ff", color: "#4338ca" }}
              />
              <Chip
                size="small"
                label={
                  savingOrder
                    ? "Salvando ordem"
                    : loading || countsLoading
                    ? "Atualizando"
                    : "Sincronizado"
                }
                style={
                  savingOrder || loading || countsLoading
                    ? { backgroundColor: "#fef3c7", color: "#92400e" }
                    : { backgroundColor: "#e2e8f0", color: "#475569" }
                }
              />
            </Box>
          </Box>

          {(loading || savingOrder) && <LinearProgress />}

          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {isAdmin && <TableCell className={classes.dragCell} />}
                  <TableCell>{i18n.t("tagsKanban.table.name")}</TableCell>
                  <TableCell>{i18n.t("tagsKanban.table.order")}</TableCell>
                  <TableCell>{i18n.t("tagsKanban.table.tickets")}</TableCell>
                  <TableCell>Automacao</TableCell>
                  {isAdmin && (
                    <TableCell align="right" className={classes.actionsCell}>
                      {i18n.t("tagsKanban.table.actions")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  filteredTags.map((tag) => {
                    const canDrag = isAdmin && !savingOrder;

                    return (
                      <TableRow
                        key={tag.id}
                        className={renderRowClassName(tag)}
                        onDragOver={(event) => handleDragOver(event, tag)}
                        onDrop={(event) => handleDrop(event, tag)}
                      >
                        {isAdmin && (
                          <TableCell className={classes.dragCell}>
                            <Tooltip title="Reordenar">
                              <span
                                className={`${classes.dragHandle} ${
                                  canDrag ? "" : classes.dragHandleDisabled
                                }`}
                                draggable={canDrag}
                                onDragStart={(event) => handleDragStart(event, tag)}
                                onDragEnd={handleDragEnd}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box className={classes.columnCell}>
                            <Box
                              className={classes.colorSwatch}
                              style={{ backgroundColor: tag.color || "#94a3b8" }}
                            />
                            <Box minWidth={0}>
                              <Typography className={classes.columnName}>{tag.name}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                ID #{tag.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <span className={classes.orderBadge}>{tag.kanbanOrder ?? 0}</span>
                        </TableCell>
                        <TableCell>{getTicketCount(tag, ticketCounts)}</TableCell>
                        <TableCell>{renderAutomation(tag)}</TableCell>
                        {isAdmin && (
                          <TableCell align="right" className={classes.actionsCell}>
                            <RowActionsMenu
                              buttonTitle="Acoes da coluna"
                              disabled={savingOrder}
                              actions={[
                                {
                                  key: "edit",
                                  label: "Editar coluna",
                                  icon: EditIcon,
                                  onClick: () => handleEditTag(tag),
                                },
                                {
                                  key: "delete",
                                  label: "Excluir coluna",
                                  icon: DeleteOutlineIcon,
                                  onClick: () => {
                                    setConfirmModalOpen(true);
                                    setDeletingTag(tag);
                                  },
                                },
                              ]}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}

                {loading && <TableRowSkeleton columns={isAdmin ? 6 : 4} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredTags.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">
                {searchParam ? "Nenhuma coluna encontrada" : "Nenhuma coluna cadastrada"}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default Tags;
